import { useState, useEffect, useMemo, type ComponentProps } from 'react';
import { useQueryClient } from 'react-query';
import {
  Loader,
  PrimaryButton,
  InternalLink,
  ExternalLink,
  ContentContainer,
} from '@bento-dental/core';
import { OutlinedTextField } from '@bento-dental/core/fields';
import { useStorageKey } from '@bento-dental/core/hooks';
import { EmployeeDialogs, EmployeeCoverageTable } from '@bento-dental/core/members';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { AddFamilyDialog, EditPlanMemberDialog } from '@bento-dental/core/employees';
import { constants } from '@bento-dental/core';
import { useEmployeeActions, EditEnrollmentsDialog } from '@bento-dental/core/members';
import CobraCancelDialog from './CobraCancelDialog';
import CobraElectDialog from './CobraElectDialog';
import { CoverageMember, EmployeeCoverage, EmployeePlanYear } from './types';

type DialogKey = string | null;

const hasFullMemberDetails = (
  member: Partial<CoverageMember> | EmployeeCoverage | null
): member is CoverageMember | EmployeeCoverage =>
  Boolean(member?.name && member?.relationship && member?.enrollment_action);

export default function EmployeeDetails() {
  const navigate = useNavigate();
  const { employeeId } = useParams<{ employeeId: string }>();
  const isEdi = useStorageKey<boolean>('isEdi');
  const [openDialog, setOpenDialog] = useState<DialogKey>(null);
  const [currentMember, setCurrentMember] = useState<
    Partial<CoverageMember> | EmployeeCoverage | null
  >(null);
  const [planYears, setPlanYears] = useState<EmployeePlanYear[]>([]);
  const queryClient = useQueryClient();
  const refreshEmployees = () => queryClient.invalidateQueries(['employees']);

  const {
    employee,
    coverage: coverageData,
    selectedPlanYearId,
    setSelectedPlanYearId,
    showLoader,
    bulkEnrollEmployee,
    refreshEmployee,
    getPlans,
  } = useEmployeeActions({
    employeeId: employeeId ? +employeeId : undefined,
    employerId: null,
    refresh: refreshEmployees,
    portal: constants.PORTALS.EMPLOYER,
    isEmployee: true,
  });

  useEffect(() => {
    if (!employee) {
      return;
    }
    const newPlanYears = employee.plan_years || [];
    const currentPlanYear = newPlanYears.find(({ is_current: current }) => Boolean(current));
    const fallbackPlanYear = newPlanYears[0];
    const shouldSelectPlanYear = currentPlanYear ?? fallbackPlanYear;
    setPlanYears(newPlanYears);
    setSelectedPlanYearId(shouldSelectPlanYear ? String(shouldSelectPlanYear.id) : '');
  }, [employee]);

  const selectedPlanYear = useMemo(
    () => planYears.find(({ id }) => String(id) === String(selectedPlanYearId)),
    [planYears, selectedPlanYearId]
  );

  const isElectCobraEnabled = Boolean(selectedPlanYear?.can_elect_cobra);
  const cancelCobraDisabled = Boolean(coverageData?.cancel_cobra_disabled);
  const cancelCobraDisabledReason = coverageData?.cancel_cobra_disabled_reason ?? '';

  const planYearOptions = planYears.map((planYearOption) => (
    <MenuItem key={`plan-years-${planYearOption.id}`} value={planYearOption.id}>
      {planYearOption.is_current ? `[Current] ${planYearOption.label}` : planYearOption.label}
    </MenuItem>
  ));

  const getActiveMember = () => (hasFullMemberDetails(currentMember) ? currentMember : null);

  const dialogMember = getActiveMember() ?? undefined;
  return (
    <ContentContainer
      title={
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h1" margin={0} padding={0} gutterBottom>
            Employee Details
          </Typography>
          {isEdi && (
            <Chip
              label="Benefit enrollment managed by EDI feed"
              variant="outlined"
              size="small"
              color="success"
            />
          )}
        </Stack>
      }
    >
      <Stack spacing={3}>
        <Breadcrumbs separator=">">
          <InternalLink to="/employees">My Employees</InternalLink>
          <Typography>Employee details</Typography>
        </Breadcrumbs>
        {showLoader ? (
          <Loader />
        ) : (
          <Box>
            <Typography color="textSecondary" fontWeight={600}>
              Name: {employee?.name}
            </Typography>
            <Typography variant="subtitle1">Member ID: {employee?.member_id}</Typography>
            <Typography variant="subtitle1">DOB: {employee?.dob}</Typography>
            <Typography variant="subtitle1">Email: {employee?.email}</Typography>
          </Box>
        )}
        <Stack direction="row" alignItems="center" width="100%">
          <Box flexGrow={1}>
            <OutlinedTextField
              select
              id="plan-years-select"
              name="planYear"
              label="Plan Year"
              value={selectedPlanYearId}
              disabled={showLoader}
              onChange={(event) => {
                setSelectedPlanYearId(event.target.value as string);
              }}
            >
              {planYearOptions}
            </OutlinedTextField>
          </Box>
          {!isEdi && (
            <Box>
              <PrimaryButton
                id="bulk-enroll-button"
                onClick={() => setOpenDialog(EmployeeDialogs.EDIT_ENROLLMENT)}
              >
                Enroll | Update | Cancel
              </PrimaryButton>
            </Box>
          )}
          <Box width="100">
            <PrimaryButton
              id="elect-cobra-button"
              onClick={() => {
                setOpenDialog(EmployeeDialogs.ELECT_COBRA);
              }}
              disabled={!isElectCobraEnabled}
            >
              Elect COBRA
            </PrimaryButton>
          </Box>
          <Tooltip title={cancelCobraDisabled ? cancelCobraDisabledReason : ''} placement="top">
            <Box width="100">
              <PrimaryButton
                id="cancel-cobra-button"
                onClick={() => {
                  setOpenDialog(EmployeeDialogs.CANCEL_COBRA);
                }}
                disabled={cancelCobraDisabled}
              >
                Cancel COBRA
              </PrimaryButton>
            </Box>
          </Tooltip>
        </Stack>
        <Divider />
        <Typography fontWeight={600}>Coverage Status</Typography>
        <EmployeeCoverageTable
          coverage={coverageData}
          openDialog={(dialog, member) => {
            setOpenDialog(dialog);
            setCurrentMember({ patient: String(member.patient) });
          }}
          isEdi={Boolean(isEdi)}
        />
        {!isEdi && (
          <PrimaryButton
            variant="outlined"
            id="add-new-family-btn"
            onClick={() => {
              setOpenDialog(EmployeeDialogs.ADD_NEW_FAMILY);
            }}
          >
            + Add family
          </PrimaryButton>
        )}
        <Divider />
        <PrimaryButton
          id="my-employees-btn"
          onClick={() => {
            navigate('/');
          }}
        >
          &lt;&lt; Return to My Employees
        </PrimaryButton>
        {!isEdi &&
          employee && [
            <AddFamilyDialog
              key="add-family-dialog"
              employee={employee as unknown as ComponentProps<typeof AddFamilyDialog>['employee']}
              open={openDialog === EmployeeDialogs.ADD_NEW_FAMILY}
              onClose={() => {
                setOpenDialog(null);
                refreshEmployee?.();
              }}
              portal={constants.PORTALS.EMPLOYER}
              corpId={selectedPlanYear?.corporation}
            />,
            <EditPlanMemberDialog
              key="edit-plan-member-dialog"
              open={openDialog === EmployeeDialogs.EDIT_EMPLOYEE}
              employee={
                employee as unknown as ComponentProps<typeof EditPlanMemberDialog>['employee']
              }
              member={
                (currentMember || undefined) as ComponentProps<
                  typeof EditPlanMemberDialog
                >['member']
              }
              onClose={() => {
                setOpenDialog(null);
                setCurrentMember(null);
              }}
              refresh={() => refreshEmployee?.()}
              portal={constants.PORTALS.EMPLOYER}
              corpId={selectedPlanYear?.corporation}
            />,
            coverageData && bulkEnrollEmployee && (
              <EditEnrollmentsDialog
                key="edit-enrollment-dialog"
                open={openDialog === EmployeeDialogs.EDIT_ENROLLMENT}
                onClose={() => {
                  setOpenDialog(null);
                }}
                planYear={selectedPlanYear}
                bulkEnrollEmployee={bulkEnrollEmployee}
                showLoader={showLoader}
                coverage={coverageData}
                getPlans={getPlans}
              />
            ),
          ]}
        {employee && (
          <>
            <CobraElectDialog
              patientId={employee.patient}
              open={openDialog === EmployeeDialogs.ELECT_COBRA}
              onClose={() => {
                setOpenDialog(null);
              }}
              refresh={refreshEmployee!}
            />
            <CobraCancelDialog
              patientId={employee.patient}
              member={currentMember || undefined}
              open={openDialog === EmployeeDialogs.CANCEL_COBRA}
              onClose={() => {
                setOpenDialog(null);
                setCurrentMember(null);
              }}
              refresh={refreshEmployee!}
            />
          </>
        )}
      </Stack>
    </ContentContainer>
  );
}
